import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format, isToday, isYesterday } from 'date-fns';
import { useActivities } from '../hooks/useActivities';
import { useToast } from '../context/ToastContext';
import { ActivityEditSheet } from '../components/activity/ActivityEditSheet';
import { ACTIVITY_META, type Activity as ActivityItem } from '../types/activity';

function formatGroupLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMM d');
}

function formatTime(date: Date): string {
  return format(date, 'h:mm a');
}

export default function Activity() {
  const { activities, loading, error, updateActivity, deleteActivity } = useActivities(100);
  const { showSuccess, showError } = useToast();
  const [editingActivity, setEditingActivity] = useState<ActivityItem | null>(null);

  // Group activities by date
  const grouped = useMemo(() => {
    const groups: { label: string; items: ActivityItem[] }[] = [];
    let currentLabel = '';
    let currentItems: ActivityItem[] = [];

    for (const activity of activities) {
      if (!activity.createdAt) continue;
      const date = activity.createdAt.toDate();
      const label = formatGroupLabel(date);

      if (label !== currentLabel) {
        if (currentItems.length > 0) {
          groups.push({ label: currentLabel, items: currentItems });
        }
        currentLabel = label;
        currentItems = [activity];
      } else {
        currentItems.push(activity);
      }
    }

    if (currentItems.length > 0) {
      groups.push({ label: currentLabel, items: currentItems });
    }

    return groups;
  }, [activities]);

  return (
    <main className="flex-1 px-6 pt-8 pb-8">
      <h2 className="font-display text-4xl italic mb-2 text-editorial-black">Activity</h2>
      <p className="font-sans text-xs uppercase tracking-[0.15em] text-gray-500 mb-8">
        Plant care log
      </p>

      <div className="editorial-divider mb-8" />

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16" role="status" aria-live="polite">
          <span
            className="material-symbols-outlined text-primary animate-pulse mb-4"
            style={{ fontSize: 32 }}
            aria-hidden="true"
          >
            timeline
          </span>
          <p className="text-sm font-serif italic text-gray-500">Loading activities...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-lg bg-danger/5 border border-danger/20 p-4 text-center" role="alert">
          <p className="text-danger font-sans text-sm">Failed to load activities.</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && activities.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span
            className="material-symbols-outlined text-gray-300 mb-4"
            style={{ fontSize: 64 }}
            aria-hidden="true"
          >
            timeline
          </span>
          <h3 className="font-display text-xl italic text-editorial-black mb-2">
            No activities yet
          </h3>
          <p className="font-serif text-gray-500 text-base max-w-xs leading-relaxed">
            Water, repot, or care for a plant to see your activity log here.
          </p>
        </div>
      )}

      {/* Activity feed */}
      {!loading && !error && grouped.length > 0 && (
        <div className="space-y-8">
          {grouped.map((group) => (
            <div key={group.label}>
              <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                {group.label}
              </h3>
              <div className="space-y-1">
                {group.items.map((activity, index) => (
                  <ActivityRow
                    key={activity.id}
                    activity={activity}
                    index={index}
                    onEdit={() => setEditingActivity(activity)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit sheet */}
      <ActivityEditSheet
        open={!!editingActivity}
        onOpenChange={(isOpen) => { if (!isOpen) setEditingActivity(null); }}
        activity={editingActivity}
        onSave={async (activityId, note) => {
          const result = await updateActivity(activityId, { note });
          if (result.success) showSuccess('Activity updated');
          else showError(result.error || 'Failed to update');
        }}
        onDelete={async (activityId) => {
          const result = await deleteActivity(activityId);
          if (result.success) showSuccess('Activity deleted');
          else showError(result.error || 'Failed to delete');
        }}
      />
    </main>
  );
}

function ActivityRow({
  activity,
  index,
  onEdit,
}: {
  activity: ActivityItem;
  index: number;
  onEdit: () => void;
}) {
  const meta = ACTIVITY_META[activity.type];
  const time = activity.createdAt ? formatTime(activity.createdAt.toDate()) : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.25 }}
    >
      <button
        type="button"
        onClick={onEdit}
        className="flex items-center gap-3 p-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors w-full text-left"
        aria-label={`Edit ${meta.label} for ${activity.plantName}`}
      >
        {/* Plant photo */}
        <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 bg-gray-100">
          <img
            src={activity.plantPhotoURL}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Activity icon */}
        <div className="p-1.5 rounded-full bg-gray-100 shrink-0">
          <span
            className={`material-symbols-outlined text-lg ${meta.color}`}
            aria-hidden="true"
          >
            {meta.icon}
          </span>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-sans text-sm text-editorial-black">
            <span className="font-semibold">{meta.label}</span>
            {' '}
            <span className="text-gray-500">{activity.plantName}</span>
          </p>
          {activity.note && (
            <p className="font-serif text-xs text-gray-500 truncate mt-0.5">
              {activity.note}
            </p>
          )}
        </div>

        {/* Time */}
        <span className="font-sans text-[11px] text-gray-400 shrink-0">
          {time}
        </span>
      </button>
    </motion.div>
  );
}
